import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'
import { useLocation } from '@reach/router'
import { useDarkMode } from '../../../hooks'
import { captureAndStoreUtmParams } from '../../../utils/thirdPartyUtils'

function SEO({ description, lang, meta, link, keywords, title, ogImage }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            siteUrl
          }
        }
      }
    `
  )

  const darkMode = useDarkMode()
  const location = useLocation()

  const { pathname } = location

  const metaDescription = description
  const { siteUrl, title: siteName } = site.siteMetadata
  const url = `${siteUrl}${pathname || '/'}`
  const socialImage = ogImage || `${siteUrl}${'/img/social_logo.jpg'}`

  useEffect(() => {
    captureAndStoreUtmParams()
  }, [])

  useEffect(() => {
    const favicon = document.querySelectorAll('[rel="icon"]')[0]
    if (darkMode) {
      if (favicon) favicon.href = '/img/favicon-dark.ico'
    } else if (favicon) favicon.href = '/img/favicon.ico'
  }, [darkMode])

  return (
    <Helmet
      htmlAttributes={{
        lang
      }}
      title={title}
      titleTemplate={`%s | ${siteName}`}
      link={link}
      meta={[
        {
          name: `description`,
          property: `description`,
          content: metaDescription
        },
        {
          property: `og:image`,
          name: `image`,
          content: socialImage
        },
        {
          name: `image`,
          content: socialImage
        },
        {
          property: `og:image:width`,
          content: `1200`
        },
        {
          property: `og:image:height`,
          content: `630`
        },
        {
          property: `og:site_name`,
          content: `LaunchDarkly`
        },
        {
          property: `og:title`,
          name: 'title',
          content: title
        },
        {
          property: `og:description`,
          name: 'description',
          content: metaDescription
        },
        {
          name: `author`,
          content: 'LaunchDarkly'
        },
        {
          property: `og:type`,
          content: `website`
        },
        {
          property: `og:url`,
          content: url
        },
        {
          name: `twitter:site`,
          property: `twitter:site`,
          content: `@LaunchDarkly`
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`
        },
        {
          name: `twitter:title`,
          property: `twitter:title`,
          content: title
        },
        {
          name: `twitter:image`,
          property: `twitter:image`,
          content: socialImage
        },
        {
          name: `twitter:description`,
          property: `twitter:description`,
          content: metaDescription
        }
      ]
        .concat(
          keywords.length > 0
            ? {
                name: `keywords`,
                content: keywords.join(`, `)
              }
            : []
        )
        .concat(meta)}
    >
      <title>{title}</title>
      <script>
        {`
          LUX=(function(){var a=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMarks)?LUX.gaMarks:[]);var d=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMeasures)?LUX.gaMeasures:[]);var j="LUX_start";var k=window.performance;var l=("undefined"!==typeof(LUX)&&LUX.ns?LUX.ns:(Date.now?Date.now():+(new Date())));if(k&&k.timing&&k.timing.navigationStart){l=k.timing.navigationStart}function f(){if(k&&k.now){return k.now()}var o=Date.now?Date.now():+(new Date());return o-l}function b(n){if(k){if(k.mark){return k.mark(n)}else{if(k.webkitMark){return k.webkitMark(n)}}}a.push({name:n,entryType:"mark",startTime:f(),duration:0});return}function m(p,t,n){if("undefined"===typeof(t)&&h(j)){t=j}if(k){if(k.measure){if(t){if(n){return k.measure(p,t,n)}else{return k.measure(p,t)}}else{return k.measure(p)}}else{if(k.webkitMeasure){return k.webkitMeasure(p,t,n)}}}var r=0,o=f();if(t){var s=h(t);if(s){r=s.startTime}else{if(k&&k.timing&&k.timing[t]){r=k.timing[t]-k.timing.navigationStart}else{return}}}if(n){var q=h(n);if(q){o=q.startTime}else{if(k&&k.timing&&k.timing[n]){o=k.timing[n]-k.timing.navigationStart}else{return}}}d.push({name:p,entryType:"measure",startTime:r,duration:(o-r)});return}function h(n){return c(n,g())}function c(p,o){for(i=o.length-1;i>=0;i--){var n=o[i];if(p===n.name){return n}}return undefined}function g(){if(k){if(k.getEntriesByType){return k.getEntriesByType("mark")}else{if(k.webkitGetEntriesByType){return k.webkitGetEntriesByType("mark")}}}return a}return{mark:b,measure:m,gaMarks:a,gaMeasures:d}})();LUX.ns=(Date.now?Date.now():+(new Date()));LUX.ac=[];LUX.cmd=function(a){LUX.ac.push(a)};LUX.init=function(){LUX.cmd(["init"])};LUX.send=function(){LUX.cmd(["send"])};LUX.addData=function(a,b){LUX.cmd(["addData",a,b])};LUX_ae=[];window.addEventListener("error",function(a){LUX_ae.push(a)});LUX_al=[];if("function"===typeof(PerformanceObserver)&&"function"===typeof(PerformanceLongTaskTiming)){var LongTaskObserver=new PerformanceObserver(function(c){var b=c.getEntries();for(var a=0;a<b.length;a++){var d=b[a];LUX_al.push(d)}});try{LongTaskObserver.observe({type:["longtask"]})}catch(e){}};
        `}
      </script>
      <script src="https://cdn.speedcurve.com/js/lux.js?id=4177336936" async defer crossOrigin="anonymous" />
      <script defer src="//cdn.bizible.com/scripts/bizible.js" />
      <script defer>
        {`
          function onPageLoad() {
            // Bing
            (function(w, d, t, r, u) {
              var f, n, i;
              (w[u] = w[u] || []),
                (f = function() {
                  var o = { ti: "17559899" };
                  (o.q = w[u]), (w[u] = new UET(o)), w[u].push("pageLoad");
                }),
                (n = d.createElement(t)),
                (n.src = r),
                (n.async = 1),
                (n.onload = n.onreadystatechange = function() {
                  var s = this.readyState;
                  (s && s !== "loaded" && s !== "complete") ||
                    (f(), (n.onload = n.onreadystatechange = null));
                }),
                (i = d.getElementsByTagName(t)[0]),
                i.parentNode.insertBefore(n, i);
            })(window, document, "script", "//bat.bing.com/bat.js", "uetq");

                // marketo
              (function() {
                var didInit = false;
                function initMunchkin() {
                  if(didInit === false) {
                    didInit = true;
                    Munchkin.init('850-KKH-319');
                  }
                }
                var s = document.createElement('script');
                s.type = 'text/javascript';
                s.async = true;
                s.src = '//munchkin.marketo.net/munchkin.js';
                s.onreadystatechange = function() {
                  if (this.readyState == 'complete' || this.readyState == 'loaded') {
                    initMunchkin();
                  }
                };
                s.onload = initMunchkin;
                document.getElementsByTagName('head')[0].appendChild(s);
              })();
          }
          if (window.attachEvent) {
            window.attachEvent("onload", onPageLoad);
          } else {
            window.addEventListener("load", onPageLoad, false);
          }
        `}
      </script>
      <script async>
        {`
          !function(q,e,v,n,t,s){if(q.qp) return; n=q.qp=function(){n.qp?n.qp.apply(n,arguments):n.queue.push(arguments);}; n.queue=[];t=document.createElement(e);t.async=!0;t.src=v; s=document.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s);}(window, 'script', 'https://a.quora.com/qevents.js');
          qp('init', '88f76bd082d74bcbba68a0366f5a4e31');
          qp('track', 'ViewContent');
        `}
      </script>
      <script async>
        {`
          !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','t2_a6eznsp0');rdt('track', 'PageVisit');
        `}
      </script>
      <noscript defer>
        {`
          <img height="1" width="1" style="display:none" src="https://q.quora.com/_/ad/88f76bd082d74bcbba68a0366f5a4e31/pixel?tag=ViewContent&noscript=1"/>
        `}
      </noscript>
      <script async src="https://www.googletagmanager.com/gtag/js?id=AW-960335887" />
      {/* AW-960335887 = Google Ads, G-31EH4XPW51 = GA4 */}
      <script>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'AW-960335887');
          gtag('config', 'G-31EH4XPW51');
       `}
      </script>
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
  description: ``
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired
}

export default SEO
